import { NgIf } from '@angular/common';
import { Component, HostBinding, NgModule } from '@angular/core';
import { ReactiveFormsModule } from '@angular/forms';
import { DateFnsAdapter, MAT_DATE_FNS_FORMATS, MatDateFnsModule } from '@angular/material-date-fns-adapter';
import { DateAdapter, MAT_DATE_FORMATS, MAT_DATE_LOCALE, MatDateFormats } from '@angular/material/core';
import { MatDatepickerInputEvent, MatDatepickerModule } from '@angular/material/datepicker';
import { MatIconModule } from '@angular/material/icon';
import { FieldType, FormlyModule } from '@ngx-formly/core';
import { FormFieldModule } from '../../wrapper/form-field/form-field.component';
import { CyTestDirective } from '@portal/directive';
import { format, isMatch } from 'date-fns';
import { DATEPICKER, EMPTY_VALUE } from '@core/data-access';
import { PortalFieldTypeConfig } from '@portal/form/data-access';
import { MatFormFieldModule } from '@angular/material/form-field';
import { enUS } from 'date-fns/locale';
import { parseDateToStartOfLocalDate } from '@core/util';

/**
 * Override MAT_DATE_FNS_FORMATS based on the default format v14.2.x
 *
 * Default format {@link https://github.com/angular/components/blob/14.2.x/src/material-date-fns-adapter/adapter/date-fns-formats.ts here}
 */
const CUSTOM_MAT_DATE_FNS_FORMATS: MatDateFormats = {
  parse: {
    dateInput: DATEPICKER.DISPLAY,
  },
  display: {
    ...MAT_DATE_FNS_FORMATS.display,
    dateInput: DATEPICKER.DISPLAY,
  },
} as const;

@Component({
  selector: 'portal-form-datepicker',
  standalone: true,
  imports: [
    NgIf,
    FormlyModule,
    ReactiveFormsModule,
    MatIconModule,
    MatDatepickerModule,
    MatDateFnsModule,
    CyTestDirective,
    MatFormFieldModule,
  ],
  templateUrl: './datepicker.component.html',
  styleUrls: ['./datepicker.component.scss'],
  providers: [
    { provide: MAT_DATE_LOCALE, useValue: enUS }, // Use United States English locale
    { provide: DateAdapter, useClass: DateFnsAdapter, deps: [MAT_DATE_LOCALE] },
    { provide: MAT_DATE_FORMATS, useValue: CUSTOM_MAT_DATE_FNS_FORMATS },
  ],
  // changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DatepickerComponent extends FieldType<PortalFieldTypeConfig> {
  protected readonly defaultValue = EMPTY_VALUE;

  @HostBinding('class') get hostClass(): string {
    return this.disabled ? 'field-value' : 'field-datepicker field-input-wrapper';
  }

  /**
   * Check if form or field is disabled
   * @returns boolean
   * @readonly
   */
  get disabled(): boolean {
    return !!this.options?.formState?.disabled || !!this.props?.disabled;
  }

  get type(): string {
    return this.disabled ? 'hidden' : 'text';
  }

  get display(): string {
    const date = this.formControl.value;

    if (!date) return this.defaultValue;

    if (date instanceof Date) {
      return format(date, DATEPICKER.DISPLAY);
    }

    if (typeof date === 'string') {
      const parsedDate = parseDateToStartOfLocalDate(date, { format: DATEPICKER.PATTERN });
      return format(parsedDate, DATEPICKER.DISPLAY);
    }

    if (typeof date === 'number') {
      return format(new Date(date), DATEPICKER.DISPLAY);
    }

    return 'Invalid Date';
  }

  // get displaySGTime(): string {
  //   const date = this.formControl.value;
  //
  //   if (!date) return this.defaultValue;
  //
  //   if (date instanceof Date) {
  //     return format(date, "yyyy-MM-dd'T'HH:mm:ss.SSSXXX", { timeZone: SINGAPORE_ZONE });
  //   }
  //
  //   if (typeof date === 'string' || typeof date === 'number') {
  //     return format(new Date(date), "yyyy-MM-dd'T'HH:mm:ss.SSSXXX", { timeZone: SINGAPORE_ZONE });
  //   }
  //
  //   return 'Invalid Date';
  // }

  addEvent(type: 'input' | 'change', event: MatDatepickerInputEvent<Date>): void {
    if (!event.value || type === 'change' || !(this.formControl.value instanceof Date)) return;
    console.log({
      key: this.field.key,
      value: event.value,
      localISO: event.value.toISOString(),
      // sgISO: parseLocalDateToISOZone(event.value, SINGAPORE_ZONE),
    });
  }
}

@NgModule({
  imports: [
    FormFieldModule,
    FormlyModule.forChild({
      types: [
        {
          name: 'datepicker',
          component: DatepickerComponent,
          wrappers: ['form-field'],
          defaultOptions: {
            props: { placeholder: 'Please Select' },
            hooks: {
              onInit: field => {
                const value = field.formControl?.value;
                if (typeof value !== 'string' || !isMatch(value, DATEPICKER.PATTERN)) return;

                // Only parse if value is date string and matches the expected pattern
                const date = parseDateToStartOfLocalDate(value, { format: DATEPICKER.PATTERN });
                console.log(field.parent?.key, field.key, value, date.toISOString());
                field.formControl?.setValue(date, { emitEvent: false });
              },
            },
          },
        },
      ],
    }),
  ],
})
export class FormDatepickerModule {}
